/* components/patterns/Tabs.jsx — навигация по вкладкам и якорям.
   ScrollRow   — горизонтально прокручиваемая строка с edge-fade по краям.
   Tabs        — варианты line/pills/segmented; при переполнении → ScrollRow.
   SectionTabs — вкладки раздела в шапке PageHeader (.seg-tab).
   TabPanel / tabPanelProps — тело вкладки (role="tabpanel"), связанное с вкладкой.
   AnchorNav   — sticky навигация по якорям раздела (TTKEditor 1–7); smooth-scroll.

   Вкладки по модели APG (kitchen-ux K-12): roving tabindex (в порядке Tab только
   активная), ←/→ по кругу, Home/End — края; у каждой вкладки id, aria-controls — туда,
   где панель есть (children у Tabs или idBase + TabPanel/tabPanelProps у вызывающего), и
   только у ВЫБРАННОЙ: панель рисуется одна — у невыбранных ссылка вела бы на несуществующий
   элемент (kitchen-ux D1a круг 2).
   Активация: Tabs — автоматическая (панели редактора показываются мгновенно),
   SectionTabs — ручная (смена вкладки раздела = новый запрос реестра; стрелка двигает
   только фокус, выбор — Enter/Space). Счётчик вкладки — `count` или `badge`.
   ХЕНДОФФ → frontend/components/common/. */

import React from 'react';
import { Icon } from '../ui/Icon.jsx';

  const { useState, useRef, useEffect, useCallback, useId } = React;

  /* ── Клавиатура и id-связки вкладок (чистые, общие для Tabs и SectionTabs) ── */
  function tabKeyTarget(key, index, count) {
    if (!(count > 0)) return null;
    switch (key) {
      case 'ArrowRight': return (index + 1) % count;
      case 'ArrowLeft': return (index - 1 + count) % count;
      case 'Home': return 0;
      case 'End': return count - 1;
      default: return null;
    }
  }
  const idPart = (v) => String(v).replace(/\s+/g, '_');
  const tabDomIds = (base, value) => ({ tab: `${base}-tab-${idPart(value)}`, panel: `${base}-panel-${idPart(value)}` });
  // Атрибуты тела вкладки для ЧУЖОГО контейнера (EntityEditor, тело страницы): spread на div.
  const tabPanelProps = (idBase, value) => {
    const ids = tabDomIds(idBase, value);
    return { role: 'tabpanel', id: ids.panel, 'aria-labelledby': ids.tab, tabIndex: 0 };
  };
  function focusTabAt(fromEl, idx) {
    const list = fromEl && typeof fromEl.closest === 'function' ? fromEl.closest('[role="tablist"]') : null;
    const tabs = list ? list.querySelectorAll('[role="tab"]') : [];
    const el = tabs[idx];
    if (el && typeof el.focus === 'function') el.focus();
  }

  /* ── ScrollRow: edge-fade при переполнении ── */
  function ScrollRow({ children, className, gap }) {
    const ref = useRef(null);
    const [edge, setEdge] = useState({ l: false, r: false });
    const update = () => {
      const el = ref.current; if (!el) return;
      setEdge({ l: el.scrollLeft > 4, r: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
    };
    useEffect(() => {
      update();
      const el = ref.current; if (!el) return;
      el.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
    }, [children]);
    return (
      <div className={'scroll-row-wrap' + (edge.l ? ' fade-l' : '') + (edge.r ? ' fade-r' : '') + (className ? ' ' + className : '')}>
        <div className="scroll-row" ref={ref} style={gap ? { gap } : undefined}>{children}</div>
      </div>
    );
  }

  /* ── Tabs ──
     children → Tabs сам рисует тело (role="tabpanel") под строкой вкладок.
     idBase   → тело рисует вызывающий: <TabPanel idBase value> или {...tabPanelProps()}. */
  function Tabs({ items = [], value, onChange, variant = 'line', className, ariaLabel = 'Вкладки',
                  idBase, activation = 'auto', children }) {
    const autoId = useId();
    const base = idBase || autoId;
    const norm = items.map((it) => (typeof it === 'string' ? { value: it, label: it } : it));
    const activeIdx = norm.findIndex((t) => t.value === value);
    const focusIdx = activeIdx >= 0 ? activeIdx : 0;
    const hasPanel = children !== undefined || !!idBase;
    const onKeyDown = (e, idx) => {
      const next = tabKeyTarget(e.key, idx, norm.length);
      if (next == null) return;
      e.preventDefault();
      focusTabAt(e.currentTarget, next);
      if (activation === 'auto' && onChange) onChange(norm[next].value);
    };
    const inner = (
      <div className={'tabs tabs--' + variant} role="tablist" aria-label={ariaLabel}>
        {norm.map((t, idx) => {
          const active = t.value === value;
          const ids = tabDomIds(base, t.value);
          const count = t.count != null ? t.count : t.badge;
          return (
            <button type="button" key={t.value} role="tab" id={ids.tab} aria-selected={active}
              aria-controls={hasPanel && active ? ids.panel : undefined} tabIndex={idx === focusIdx ? 0 : -1}
              className={'tab' + (active ? ' is-active' : '')} onClick={() => onChange && onChange(t.value)}
              onKeyDown={(e) => onKeyDown(e, idx)}>
              {t.icon && <Icon name={t.icon} size={16} />}
              <span className="tab__label">{t.label}</span>
              {count != null && <span className="tab__count num">{count}</span>}
            </button>
          );
        })}
      </div>
    );
    const row = <ScrollRow className={'tabs-scroll tabs-scroll--' + variant + (className ? ' ' + className : '')}>{inner}</ScrollRow>;
    if (children === undefined) return row;
    return <>{row}<div className="tabs__panel" {...tabPanelProps(base, value)}>{children}</div></>;
  }

  /* ── TabPanel: тело вкладки, когда его рисует вызывающий (Tabs с тем же idBase) ── */
  function TabPanel({ idBase, value, children, className }) {
    return <div className={'tabs__panel' + (className ? ' ' + className : '')} {...tabPanelProps(idBase, value)}>{children}</div>;
  }

  /* ── SectionTabs: вкладки раздела в шапке страницы (PageHeader) ──
     Горизонтальная лента с edge-fade (03-adaptive §391): градиент у краёв, переключаемый
     по фактической прокрутке (.scroll-row-wrap.fade-l/.fade-r). tabs: [{id,label,count|badge,onb}]. */
  function SectionTabs({ tabs = [], activeTab, onTab, label, idBase, activation = 'manual' }) {
    const rowRef = useRef(null);
    const [fade, setFade] = useState({ l: false, r: false });
    const autoId = useId();
    const base = idBase || autoId;
    const update = useCallback(() => {
      const el = rowRef.current;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      setFade({ l: el.scrollLeft > 1, r: el.scrollLeft < maxScroll - 1 });
    }, []);
    useEffect(() => {
      update();
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }, [update, tabs]);
    const activeIdx = tabs.findIndex((t) => t.id === activeTab);
    const focusIdx = activeIdx >= 0 ? activeIdx : 0;
    const onKeyDown = (e, idx) => {
      const next = tabKeyTarget(e.key, idx, tabs.length);
      if (next == null) return;
      e.preventDefault();
      focusTabAt(e.currentTarget, next);
      if (activation === 'auto' && onTab) onTab(tabs[next].id);
    };
    const cls = 'scroll-row-wrap' + (fade.l ? ' fade-l' : '') + (fade.r ? ' fade-r' : '');
    return (
      <div className={cls}>
        <div className="page-header__tabs scroll-row" role="tablist" aria-label={label} ref={rowRef} onScroll={update}>
          {tabs.map((t, idx) => {
            const active = activeTab === t.id;
            const ids = tabDomIds(base, t.id);
            const count = t.count != null ? t.count : t.badge;
            return (
              // data-through-scrim: вкладка раздела видна под затемнением редактора — клик по ней
              // закрывает его через сторожа и выполняет переход (lib/scrimPass.js, QA Г3 I07)
              <button type="button" key={t.id} role="tab" id={ids.tab} aria-selected={active}
                aria-controls={idBase && active ? ids.panel : undefined} tabIndex={idx === focusIdx ? 0 : -1}
                data-onb={t.onb} data-through-scrim="" className={'seg-tab' + (active ? ' is-active' : '')}
                onClick={() => onTab && onTab(t.id)} onKeyDown={(e) => onKeyDown(e, idx)}>
                {t.label}
                {count != null && <span className="seg-tab__count num">{count}</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  /* ── AnchorNav: sticky по якорям ── */
  function AnchorNav({ items = [], orientation = 'vertical', offset = 80, className }) {
    const [active, setActive] = useState(items[0] && items[0].id);
    useEffect(() => {
      const ids = items.map((i) => i.id);
      const onScroll = () => {
        let cur = ids[0];
        for (const id of ids) {
          const el = document.getElementById(id);
          if (el && el.getBoundingClientRect().top - offset <= 1) cur = id;
        }
        setActive(cur);
      };
      const scroller = document.querySelector('[data-scroll-main]') || window;
      scroller.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return () => scroller.removeEventListener('scroll', onScroll);
    }, [items, offset]);
    const go = (e, id) => {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) { const top = el.getBoundingClientRect().top + window.scrollY - offset; window.scrollTo({ top, behavior: 'smooth' }); }
      setActive(id);
    };
    return (
      <nav className={'anchor-nav anchor-nav--' + orientation + (className ? ' ' + className : '')} aria-label="Разделы">
        {items.map((it) => (
          <a key={it.id} href={'#' + it.id} onClick={(e) => go(e, it.id)}
            className={'anchor-nav__item' + (active === it.id ? ' is-active' : '')} aria-current={active === it.id ? 'true' : undefined}>
            {it.num != null && <span className="anchor-nav__num num">{it.num}</span>}
            <span className="anchor-nav__label">{it.label}</span>
          </a>
        ))}
      </nav>
    );
  }

export { ScrollRow };
export { Tabs };
export { TabPanel };
export { SectionTabs };
export { AnchorNav };
export { tabKeyTarget, tabPanelProps };
